// Tryb — listing and venue wizard.
//
// Fifteen steps for a listing, three of them for a venue on its own. The rail
// counts required answers per step; the last screen is a review that will not
// let anything be published while something is still missing.

import { useState } from "react";
import { WIZARD, isSeries } from "@shared/fields.js";
import { countReq, hasErrors } from "@shared/validation.js";
import { Button, Tag } from "../ds/index.js";
import Brand from "../components/Brand.jsx";
import { FieldCell } from "../components/TrybField.jsx";
import { useStudio } from "../lib/studioContext.js";
import { useFieldViews } from "../lib/useFieldViews.js";
import { api } from "../lib/api.js";

const mono = { fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "var(--tracking-wider)", textTransform: "uppercase" };

export default function Wizard({ mode, onClose, onOpenCover, onPublished }) {
  const { vals, venues, trybs, setField, showToast, applyStudio, withFlush } = useStudio();
  const { fieldViews } = useFieldViews({ onOpenCover });

  const [step, setStep] = useState(() => (mode === "listing" && vals["A3.02"] && vals["A3.01"] ? 1 : 0));
  const [attempted, setAttempted] = useState({});

  const isVenue = mode === "venue";
  const steps = isVenue ? WIZARD.filter((s) => s.venue) : WIZARD;
  const isReview = step >= steps.length;
  const current = steps[step];

  const allIds = steps.flatMap((s) => s.ids);
  const total = countReq(allIds, vals);
  const missing = total.total - total.done;

  const next = () =>
    withFlush(async () => {
      try {
        const { ok } = await api.checkWizardStep(current.id, mode);
        if (!ok) {
          setAttempted((a) => ({ ...a, [current.id]: true }));
          return;
        }
        setAttempted((a) => ({ ...a, [current.id]: false }));
        setStep((s) => s + 1);
      } catch (e) {
        showToast("Couldn’t check that step", e.message);
      }
    });

  const publish = () =>
    withFlush(async () => {
      try {
        const res = isVenue ? await api.saveVenue() : await api.publishListing();
        applyStudio(res);
        onPublished(
          isVenue
            ? { page: "venues", title: "Venue saved", desc: "Next time you list here, you’ll skip three whole steps." }
            : {
                page: "trybs",
                // Submitting is not going live: every tryb, and every edit to one, is read by the team first.
                title: res.updated ? "Sent for review again" : "Sent for review",
                desc: "Our team reads every tryb before it goes on tryb.in. You’ll see where it stands under your trybs.",
              },
        );
      } catch (e) {
        // An unverified host keeps the draft and is sent to verification.
        if (e.status === 403 && e.body?.needs === "verification") {
          applyStudio(e.body);
          onPublished({ page: "kyc", title: e.message, desc: e.hint });
          return;
        }
        showToast(isVenue ? "The venue isn’t complete yet" : "This tryb isn’t complete yet", e.hint || e.message);
        setStep(steps.length);
      }
    });

  const close = () =>
    withFlush(() => {
      onClose();
      showToast("Draft saved", "It’ll be here when you come back.");
    });

  const showErrors = !!attempted[current?.id];
  const stepCount = current ? countReq(current.ids, vals) : { done: 0, total: 0 };

  return (
    <div
      data-screen-label="Listing wizard"
      style={{ position: "fixed", inset: 0, zIndex: 40, background: "var(--surface-page)", display: "flex", animation: "trybRise var(--dur-slow) var(--ease-out)" }}
    >
      <aside
        className="tryb-rail"
        style={{
          width: 300,
          flex: "none",
          background: "var(--ink-800)",
          color: "var(--sand-75)",
          padding: "24px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 22,
          overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Brand size={32} label={null} />
            <div style={{ ...mono, opacity: 0.7 }}>{isVenue ? "New venue" : trybs.length ? "Edit tryb" : "New tryb"}</div>
          </div>
        </div>

        <div>
          <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-xl)", fontWeight: 800, letterSpacing: "var(--tracking-tight)", lineHeight: 1.1 }}>
            {isVenue ? "Save it once. Reuse it forever." : vals["B1.01"] || "Your first tryb"}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
            <div style={{ flex: 1, height: 6, borderRadius: 999, background: "color-mix(in srgb, var(--sand-75) 16%, transparent)", overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  background: "var(--ember-500)",
                  width: `${total.total ? Math.round((total.done / total.total) * 100) : 0}%`,
                  transition: "width var(--dur-slow) var(--ease-out)",
                }}
              />
            </div>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, opacity: 0.8, whiteSpace: "nowrap" }}>
              {total.done}/{total.total} needed
            </span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {steps.map((x, i) => {
            const c = countReq(x.ids, vals);
            const on = i === step;
            const done = c.done === c.total;
            return (
              <button
                key={x.id}
                onClick={() => setStep(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  padding: "8px 10px",
                  borderRadius: "var(--radius-sm)",
                  fontFamily: "var(--font-body)",
                  fontSize: "var(--text-sm)",
                  background: on ? "color-mix(in srgb, var(--sand-75) 12%, transparent)" : "transparent",
                  color: "var(--sand-75)",
                  fontWeight: on ? 700 : 500,
                }}
              >
                <span
                  style={{
                    width: 22, height: 22, borderRadius: "50%", flex: "none",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "var(--font-mono)", fontSize: 10,
                    background: done ? "var(--sage-600)" : on ? "var(--ember-500)" : "color-mix(in srgb, var(--sand-75) 14%, transparent)",
                    color: "var(--sand-50)",
                  }}
                >
                  {done ? "✓" : i + 1}
                </span>
                <span style={{ flex: 1 }}>{x.title}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, opacity: 0.65 }}>
                  {c.done}/{c.total}
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 40px", borderBottom: "1px solid var(--border-subtle)" }}>
          <span style={{ ...mono, color: "var(--text-muted)" }}>
            {isReview ? `Review · ${steps.length} steps` : `Step ${step + 1} of ${steps.length}`}
          </span>
          <Button variant="ghost" size="sm" onClick={close}>
            Save draft and close
          </Button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "40px 40px 120px" }}>
          <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 28 }}>
            {isReview ? (
              <Review steps={steps} isVenue={isVenue} missing={missing} total={total} vals={vals} onGo={setStep} />
            ) : (
              <>
                <div>
                  <h1 style={{ margin: "0 0 10px", fontFamily: "var(--font-display)", fontSize: "var(--text-3xl)", fontWeight: 800, letterSpacing: "var(--tracking-tight)", lineHeight: 1.05 }}>
                    {current.title}
                  </h1>
                  <p style={{ margin: 0, fontSize: "var(--text-md)", color: "var(--text-secondary)", lineHeight: 1.55 }}>{current.blurb}</p>
                </div>

                {current.id === "venue" && !isVenue && venues.length > 0 && (
                  <div
                    style={{
                      background: "var(--sage-100)",
                      border: "1px solid var(--sage-200)",
                      borderRadius: "var(--radius-lg)",
                      padding: "18px 22px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--sage-700)" }}>Use a saved venue</span>
                    {venues.map((v) => (
                      <Tag
                        key={v.id}
                        selected={vals["C1.03"] === v.name}
                        onClick={() => Object.entries(v.vals).forEach(([k, val]) => setField(k, val))}
                      >
                        {v.name}
                      </Tag>
                    ))}
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "22px 20px" }}>
                  {fieldViews(current.ids, showErrors).map((f) => (
                    <FieldCell key={f.id} f={f} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div
          style={{
            padding: "16px 40px",
            borderTop: "1px solid var(--border-subtle)",
            background: "var(--surface-page)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}>
            Back
          </Button>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {!isReview && showErrors && hasErrors(current.ids, vals) && (
              <span style={{ fontSize: "var(--text-sm)", color: "var(--crimson-600)", fontWeight: 600 }}>
                {stepCount.total - stepCount.done} to fix on this step
              </span>
            )}
            {isReview ? (
              <Button variant="accent" size="lg" onClick={publish} disabled={missing > 0}>
                {isVenue ? "Save venue" : trybs.length ? "Save and resubmit" : "Send for review"}
              </Button>
            ) : (
              <Button variant="primary" size="lg" onClick={next}>
                {step === steps.length - 1 ? "Review it all" : "Next"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Review({ steps, isVenue, missing, total, vals, onGo }) {
  return (
    <>
      <div>
        <h1 style={{ margin: "0 0 10px", fontFamily: "var(--font-display)", fontSize: "var(--text-3xl)", fontWeight: 800, letterSpacing: "var(--tracking-tight)", lineHeight: 1.05 }}>
          {missing === 0 ? (isVenue ? "Venue’s ready to save." : "Looks good. Ready when you are.") : `${missing} thing${missing === 1 ? "" : "s"} left.`}
        </h1>
        <p style={{ margin: 0, fontSize: "var(--text-md)", color: "var(--text-secondary)", lineHeight: 1.55 }}>
          {isVenue
            ? "Every tryb you run here will skip these steps from now on."
            : missing === 0
              ? `All ${total.total} required answers are in. Certification is a separate thing and never holds up publishing.`
              : "The required bits are what let someone book with confidence. Certification is separate and never holds up publishing."}
        </p>
      </div>

      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--shadow-sm)",
          padding: "8px 24px",
        }}
      >
        {steps.map((s, i) => {
          const c = countReq(s.ids, vals);
          const done = c.done === c.total;
          return (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderTop: "1px solid var(--border-subtle)" }}>
              <span
                style={{
                  width: 26, height: 26, borderRadius: "50%", flex: "none",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "var(--font-mono)", fontSize: 11,
                  background: done ? "var(--sage-100)" : "var(--honey-100)",
                  color: done ? "var(--sage-700)" : "var(--honey-600)",
                }}
              >
                {done ? "✓" : i + 1}
              </span>
              <span style={{ flex: 1, fontWeight: 600 }}>{s.title}</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: done ? "var(--text-muted)" : "var(--ember-700)" }}>
                {done ? "Done" : `${c.total - c.done} to fill`}
              </span>
              <Button variant="ghost" size="sm" onClick={() => onGo(i)}>
                {done ? "Look again" : "Fix"}
              </Button>
            </div>
          );
        })}
      </div>

      <div
        style={{
          background: "var(--honey-100)",
          border: "1px solid var(--honey-300)",
          borderRadius: "var(--radius-lg)",
          padding: "18px 22px",
          fontSize: "var(--text-sm)",
          lineHeight: 1.55,
          color: "var(--text-secondary)",
        }}
      >
        {isVenue
          ? "Our team will check the pin against your photo of the door before your first night."
          : `Members will see “${vals["B4.02"] || 5} needed to run, ${vals["B4.01"] || 8} seats” and when you’ll make the call — never a fill rate. ${
              !isSeries(vals) ? "" : vals["B2.10"] ? "Next season will queue up automatically." : "Next season waits for you to confirm it."
            }`}
      </div>
    </>
  );
}
